import { tryParseJSONObject } from '@fox/atoms';
import {
	BPointAddCreditCard,
	BPointAddDirectDebit,
	CreditCardSelector,
	DirectDebitSelector,
	FatZebraAddCreditCard,
	FinancialConfigCommon,
	NabTransactAddCreditCard,
	PAYMENT_SELECTOR_PREFIX,
	PayWayAddCreditCard,
	PaymentAddComponentProps,
	PaymentAddProps,
	PaymentMethod,
	PaymentSelectorProps,
	ProviderSelection,
	StripeAddCreditCard,
	getAllowedCurrencies,
	paymentExtension
} from '@fox/finance';
import { IExtensionRegistration, api } from 'fox-extensions';
import React from 'react';
import { SwiftFoxFinancialConfigResult } from '../financial-details/contracts';
import { CreditCardDisplay } from '../financial-details/payment-details/credit-card-display';
import { DirectDebitDisplay } from '../financial-details/payment-details/direct-debit-display';
import { convertMemberPortalToSfMethodEnumValue } from '../financial-details/shared';
import { CardIntentWrapper } from './components/card-intent-wrapper';
import { CreateDebit } from './components/create-debit';
import { DebitIntentWrapper } from './components/debit-intent-wrapper';
import { AddPaymentWrapperProps, ProviderExtensionNames } from './contracts';

let financialConfigInitialized = false;

const PAYMENT_METHOD_ADD_COMPONENTS: Record<
	string,
	{
		component?: React.FC<PaymentAddComponentProps>;
		wrapper?: React.FC<AddPaymentWrapperProps>;
		standAloneComponent?: React.FC<PaymentAddProps>;
	}
> = {
	[paymentExtension(ProviderExtensionNames.BPoint, PaymentMethod.CreditCard, 'add')]: {
		component: BPointAddCreditCard,
		wrapper: CardIntentWrapper
	},
	[paymentExtension(ProviderExtensionNames.Stripe, PaymentMethod.CreditCard, 'add')]: {
		component: StripeAddCreditCard,
		wrapper: CardIntentWrapper
	},
	[paymentExtension(ProviderExtensionNames.NabTransact, PaymentMethod.CreditCard, 'add')]: {
		component: NabTransactAddCreditCard,
		wrapper: CardIntentWrapper
	},
	[paymentExtension(ProviderExtensionNames.FatZebra, PaymentMethod.CreditCard, 'add')]: {
		component: FatZebraAddCreditCard,
		wrapper: CardIntentWrapper
	},
	[paymentExtension(ProviderExtensionNames.BPoint, PaymentMethod.DirectDebit, 'add')]: {
		component: BPointAddDirectDebit,
		wrapper: DebitIntentWrapper
	},
	[paymentExtension(ProviderExtensionNames.PayWay, PaymentMethod.CreditCard, 'add')]: {
		component: PayWayAddCreditCard,
		wrapper: CardIntentWrapper
	},
	[paymentExtension(ProviderExtensionNames.FatZebra, PaymentMethod.DirectDebit, 'add')]: {
		standAloneComponent: CreateDebit
	},
	[paymentExtension(ProviderExtensionNames.AusBecs, PaymentMethod.DirectDebit, 'add')]: {
		standAloneComponent: CreateDebit
	}
};

const initFinanceExtensions = (extContext: IExtensionRegistration, providers: ProviderSelection[]) => {
	for (const provider of providers) {
		const paymentAdd =
			PAYMENT_METHOD_ADD_COMPONENTS[
				paymentExtension(provider.providerExtension, convertMemberPortalToSfMethodEnumValue(provider.method), 'add')
			];
		if (paymentAdd) {
			extContext.componentExtension<PaymentAddProps>(
				paymentExtension(provider.providerExtension, convertMemberPortalToSfMethodEnumValue(provider.method), 'add'),
				props => {
					const WrapperComponent = paymentAdd.wrapper;
					const Component = paymentAdd.component;
					const StandAloneComponent = paymentAdd.standAloneComponent;
					if (StandAloneComponent) {
						return <StandAloneComponent {...props} />;
					}
					return <WrapperComponent component={Component} {...props} />;
				}
			);
		}
	}

	extContext.componentExtension<PaymentSelectorProps>(`${PAYMENT_SELECTOR_PREFIX}.${PaymentMethod.CreditCard}`, props => {
		return <CreditCardSelector creditCardComponent={CreditCardDisplay} {...props} />;
	});
	extContext.componentExtension<PaymentSelectorProps>(`${PAYMENT_SELECTOR_PREFIX}.${PaymentMethod.DirectDebit}`, props => (
		<DirectDebitSelector directDebitComponent={DirectDebitDisplay} {...props} />
	));
};

export { initFinanceExtensions };

export const initiaLisePaymentMethods = async (
	contentId: string,
	requestToken: string,
	paymentMethods?: string[],
	paymentProviders?: string[]
): Promise<{
	providers: ProviderSelection[];
	currencies: {
		allowed: string[];
		default: string;
	};
	defaultTaxRate?: number;
	taxLabel?: string;
}> => {
	let queryString = '';

	if (paymentProviders != null) {
		for (const item of paymentProviders) {
			queryString += `&allowedProviders=${item}`;
		}
	}
	if (paymentMethods != null) {
		for (const item of paymentMethods) {
			queryString += `&allowedMethods=${item}`;
		}
	}
	const financialConfigRes = await fetch(`/Umbraco/Api/Membership/FinancialConfig?contentId=${contentId}${queryString}`, {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			RequestVerificationToken: requestToken
		}
	});
	if (financialConfigRes.ok) {
		const result = await financialConfigRes.json();
		if (result.success === true) {
			const financialConfig: SwiftFoxFinancialConfigResult = result.result;
			if (financialConfigInitialized === false) {
				initFinanceExtensions({ ...api, config: {} }, financialConfig.providers);
				financialConfigInitialized = true;
			}
			const parsed = tryParseJSONObject<FinancialConfigCommon>(financialConfig?.common, {});
			const stripped = JSON.stringify({
				allowedCurrencies: parsed.allowedCurrencies,
				defaults: { currency: parsed.defaults?.currency }
			});
			return {
				providers: financialConfig.providers,
				currencies: getAllowedCurrencies(stripped),
				defaultTaxRate: parsed.defaults?.taxRate,
				taxLabel: parsed.taxLabel
			};
		}
	} else {
		return null;
	}
};
